// frontend/src/components/staff/BroadcastHistoryPage.js
//
// MAILCHIMP-COMMS-001-C — Operational Broadcast Send-History view (FE).
// Route: /staff/communications/history  (mounted in App.js).
//
// Story:    docs/backlog/MAILCHIMP-COMMS-001-C-fe-broadcast-history-view.md
// Tech doc: docs/architecture/mailchimp-comms-001-A-tech.md (§"State Management",
//           §"GET /api/comms/broadcast", §"GET /api/comms/broadcast/:id")
// Visual:   docs/design/MAILCHIMP-COMMS-001-A-design.md (§5 Send History page,
//           §5.4 Send-detail drawer)
//
// Scope (Slice 1A):
//   - RBAC admin + staff + mt (gated at the App.js route + BE re-check via
//     authorizeBroadcastSender; staff queries are server-scoped to
//     senderUserId=self regardless of input)
//   - History table reading GET /api/comms/broadcast — paginated via the
//     BE's createdAt cursor (Marcus tech doc); "Load more" appends results
//   - Row click opens a right-side detail drawer reading
//     GET /api/comms/broadcast/:id
//   - Empty / loading / error states per Zuri spec §5 + §7
//   - Honors the `?fresh=<broadcastLogId>` query param the compose page
//     hands off after a successful send: highlights the matching row with
//     a 2-second gold-tint flash (Zuri spec §3.7 + §5.4 navigation hook)
//
// Production-safety: pure-additive new route + new component. Zero edits
// to existing pages, services, or routes beyond the new sidebar link and
// the post-send navigate() in BroadcastComposePage (also additive). Single
// revert: drop the route + sidebar link + the navigate() line = clean
// rollback. NOT a Customer-Facing Gate (R-020) — internal-only audit.
//
// State management: codebase has NO TanStack Query (verified in
// BroadcastComposePage / AdminStaffAvailabilityDashboard). We honor the
// tech doc's cache-key semantics via plain useState + a single
// `fetchPage` function keyed on the cursor state, re-fired on refresh /
// load-more.
//
// BE response shape reminder (commsController.listBroadcasts +
// getBroadcast — PR #156 merged):
//   list ->
//     { broadcasts: [{ broadcastLogId, senderUserId, senderRole, subject,
//                      composeMode, templateId, recipientCount, status,
//                      deliveryStats: { succeeded, failed, skippedOptOut,
//                                       skippedNoEmail }, startedAt,
//                      completedAt, createdAt }],
//       nextCursor: string|null }
//   getById -> same fields + bodyTextSnapshot, bodyHtmlSnapshot,
//              recipientSnapshot, failures[], providerMessageIds[],
//              updatedAt
//
// BE-not-yet-shipped fields (Slice 1A reservations — render "—" + reserve
// data-testids for forward compat per AC Scenarios 3, 11):
//   - templateName (only templateId is in the response; no templates
//     endpoint yet)
//   - senderDisplayName (only senderUserId + senderRole; render "Me"
//     for own broadcasts, role label otherwise)
//   - openRate / clickRate (Mailchimp stats not yet wired; BE owns the
//     fetch per MAILCHIMP-COMMS-001-A Scenario 15)
//   - mailchimpCampaignId (no provider campaign id surfaced yet;
//     spec §5.4 deep-link omitted accordingly)

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaPaperPlane,
  FaSync,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { useAuth } from "../AuthContext";
import { broadcastAPI } from "../../services/api";
import "./BroadcastHistoryPage.css";

const STUDIO_TZ = "America/Phoenix";
const STUDIO_TZ_LABEL = "MST";

// Per-page size for the history table. The BE caps at 100 but the spec
// recommends 50 per Scenario 9.
const PAGE_LIMIT = 50;

// Highlight duration for the post-send "fresh" row flash (Zuri spec §3.7
// "2-second gold flash" carries over to history via the ?fresh= hand-off).
const FRESH_HIGHLIGHT_MS = 2000;

// ----- Pure helpers (testable) ------------------------------------------

/**
 * Format an ISO timestamp in America/Phoenix with explicit "MST" label.
 * Returns "" for null/undefined/invalid input so empty cells render as
 * "—" via the caller's fallback rather than NaN/Invalid date.
 */
export function formatPhoenixDateTime(iso) {
  if (!iso) return "";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "";
  return `${m.format("MMM D, YYYY, h:mm A")} ${STUDIO_TZ_LABEL}`;
}

/**
 * Two-line stacked cell: date on top, time + MST below.
 * Returned as { date, time } so the table can render the stack semantically.
 */
export function splitPhoenixDateTime(iso) {
  if (!iso) return { date: "", time: "" };
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return { date: "", time: "" };
  return {
    date: m.format("MMM D, YYYY"),
    time: `${m.format("h:mm A")} ${STUDIO_TZ_LABEL}`,
  };
}

/**
 * Derive a human-readable sender label given a broadcast row and the
 * current viewer's userId. Returns:
 *   - "Me" when the broadcast is the viewer's own send
 *   - "Admin" / "Staff" / "Massage Therapist" otherwise based on senderRole
 *   - "Unknown" as the final fallback so the cell never renders empty
 *
 * BE-not-yet-shipped: a senderDisplayName field would replace this; until
 * then the role label is the audit-safe display. Data-testid reserved.
 */
export function formatSenderLabel(broadcast, viewerUserId) {
  if (!broadcast) return "Unknown";
  if (
    viewerUserId &&
    broadcast.senderUserId &&
    String(broadcast.senderUserId) === String(viewerUserId)
  ) {
    return "Me";
  }
  const role = broadcast.senderRole;
  if (role === "admin") return "Admin";
  if (role === "staff") return "Staff";
  if (role === "massage_therapist") return "Massage Therapist";
  return "Unknown";
}

/**
 * Map a BroadcastLog.status to a display-pill descriptor:
 *   { label, tone } where tone is one of "delivered", "partial", "failed",
 *   "pending". Tone drives the CSS class for the pill background tint.
 *
 * "pending" + "in_progress" both render "In progress" (gray).
 * "completed" renders "Delivered" (green) unless deliveryStats.failed > 0
 *   OR succeeded === 0, in which case it falls back to "Partial" / "Failed".
 * "completed_with_errors" renders "Partial" (amber) when succeeded > 0,
 *   else "Failed" (red).
 */
export function statusPillFor(broadcast) {
  if (!broadcast) return { label: "Unknown", tone: "pending" };
  const { status, deliveryStats } = broadcast;
  const succeeded = deliveryStats?.succeeded || 0;
  const failed = deliveryStats?.failed || 0;

  if (status === "pending" || status === "in_progress") {
    return { label: "In progress", tone: "pending" };
  }
  if (status === "completed") {
    if (failed === 0 && succeeded > 0) return { label: "Delivered", tone: "delivered" };
    if (succeeded === 0) return { label: "Failed", tone: "failed" };
    return { label: "Partial", tone: "partial" };
  }
  if (status === "completed_with_errors") {
    if (succeeded === 0) return { label: "Failed", tone: "failed" };
    return { label: "Partial", tone: "partial" };
  }
  return { label: "Unknown", tone: "pending" };
}

/**
 * Compact delivery-stats sub-line under the status pill, per Zuri spec
 * §5.3 ("(25/27 OK)" or "(Mailchimp 4xx)" shorthand). Returns "" when
 * stats are absent so the caller can omit the row entirely.
 */
export function statsSubline(broadcast) {
  const stats = broadcast?.deliveryStats;
  if (!stats) return "";
  const succeeded = stats.succeeded || 0;
  const failed = stats.failed || 0;
  const total = succeeded + failed + (stats.skippedOptOut || 0) + (stats.skippedNoEmail || 0);
  if (total === 0) return "";
  return `(${succeeded}/${total} OK${failed > 0 ? `, ${failed} failed` : ""})`;
}

/**
 * Truncate a subject for the table cell — keeps tooltip-friendly behavior
 * (full subject in title attr). Soft cap at 60 chars per Zuri spec §5.3.
 */
export function truncateSubject(subject, max = 60) {
  if (!subject) return "—";
  if (subject.length <= max) return subject;
  return `${subject.slice(0, max - 1)}…`;
}

// ----- Detail drawer subcomponent ---------------------------------------

const BroadcastDetailDrawer = ({ broadcastLogId, onClose, isAdmin }) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [detail, setDetail] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    broadcastAPI
      .getById(broadcastLogId)
      .then((res) => {
        if (cancelled) return;
        setDetail(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.message ||
          err?.userMessage ||
          "Couldn't load broadcast detail.";
        setError(message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [broadcastLogId]);

  // Close on Escape per Zuri spec §13 (drawer focus-trap + Escape close).
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="broadcast-history-drawer-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="broadcast-history-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Broadcast detail"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="broadcast-history-drawer-header">
          <h2>Send detail</h2>
          <button
            type="button"
            className="broadcast-history-drawer-close"
            aria-label="Close detail"
            onClick={onClose}
          >
            <FaTimes aria-hidden="true" />
          </button>
        </header>

        <div className="broadcast-history-drawer-body">
          {loading && (
            <div
              className="broadcast-history-drawer-loading"
              role="status"
              aria-live="polite"
            >
              Loading send detail…
            </div>
          )}

          {error && !loading && (
            <div className="broadcast-history-drawer-error" role="alert">
              <FaExclamationTriangle aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {detail && !loading && !error && (
            <>
              <section className="broadcast-history-drawer-section">
                <div className="broadcast-history-drawer-label">Sent</div>
                <div className="broadcast-history-drawer-value">
                  {formatPhoenixDateTime(detail.createdAt) || "—"}
                </div>
              </section>

              <section className="broadcast-history-drawer-section">
                <div className="broadcast-history-drawer-label">Subject</div>
                <div className="broadcast-history-drawer-value">
                  {detail.subject || "—"}
                </div>
              </section>

              <section className="broadcast-history-drawer-section">
                <div className="broadcast-history-drawer-label">
                  Compose mode
                </div>
                <div className="broadcast-history-drawer-value">
                  {detail.composeMode === "template"
                    ? "Template"
                    : detail.composeMode === "freeform"
                    ? "Freeform"
                    : "—"}
                </div>
              </section>

              <section className="broadcast-history-drawer-section">
                <div className="broadcast-history-drawer-label">
                  Recipients ({detail.recipientCount || 0})
                </div>
                <div className="broadcast-history-drawer-recipients">
                  {(detail.recipientSnapshot || [])
                    .slice(0, 5)
                    .map((r, idx) => (
                      <span
                        key={`${r.email || idx}-${idx}`}
                        className="broadcast-history-drawer-recipient"
                      >
                        {r.email}
                      </span>
                    ))}
                  {(detail.recipientSnapshot || []).length > 5 && (
                    <span className="broadcast-history-drawer-recipient-more">
                      +{(detail.recipientSnapshot || []).length - 5} more
                    </span>
                  )}
                </div>
                {!isAdmin && (
                  <div className="broadcast-history-drawer-hint">
                    Staff view shows recipient emails only.
                  </div>
                )}
              </section>

              <section className="broadcast-history-drawer-section">
                <div className="broadcast-history-drawer-label">Delivery</div>
                <ul className="broadcast-history-drawer-stats">
                  <li>
                    Sent successfully:{" "}
                    <strong>{detail.deliveryStats?.succeeded ?? 0}</strong>
                  </li>
                  <li>
                    Failed:{" "}
                    <strong>{detail.deliveryStats?.failed ?? 0}</strong>
                  </li>
                  <li>
                    Skipped (opted out):{" "}
                    <strong>
                      {detail.deliveryStats?.skippedOptOut ?? 0}
                    </strong>
                  </li>
                  <li>
                    Skipped (no email):{" "}
                    <strong>
                      {detail.deliveryStats?.skippedNoEmail ?? 0}
                    </strong>
                  </li>
                </ul>
                {/* Mailchimp open/click stats are not yet wired in the BE
                    (PR #156). Slot reserved for forward compat — Scenario
                    11 of the story specifies "—" rather than "0%". */}
                <ul className="broadcast-history-drawer-stats broadcast-history-drawer-stats--placeholder">
                  <li data-testid="detail-open-rate">
                    Open rate: <strong>—</strong>
                  </li>
                  <li data-testid="detail-click-rate">
                    Click rate: <strong>—</strong>
                  </li>
                </ul>
              </section>

              {Array.isArray(detail.failures) && detail.failures.length > 0 && (
                <section className="broadcast-history-drawer-section">
                  <div className="broadcast-history-drawer-label">
                    Failures ({detail.failures.length})
                  </div>
                  <ul className="broadcast-history-drawer-failures">
                    {detail.failures.slice(0, 25).map((f, idx) => (
                      <li key={`failure-${idx}`}>
                        <span className="broadcast-history-drawer-failure-email">
                          {f.email}
                        </span>
                        <span className="broadcast-history-drawer-failure-reason">
                          {f.reason || "Unknown error"}
                        </span>
                      </li>
                    ))}
                    {detail.failures.length > 25 && (
                      <li className="broadcast-history-drawer-failure-more">
                        +{detail.failures.length - 25} more — see server logs
                        for full failure trail.
                      </li>
                    )}
                  </ul>
                </section>
              )}

              {detail.bodyTextSnapshot && (
                <section className="broadcast-history-drawer-section">
                  <div className="broadcast-history-drawer-label">
                    Message body
                  </div>
                  <pre className="broadcast-history-drawer-body-text">
                    {detail.bodyTextSnapshot}
                  </pre>
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

// ----- Page component ---------------------------------------------------

const BroadcastHistoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const viewerUserId = user?.id || user?._id || null;

  // Parse the ?fresh=<id> hand-off from BroadcastComposePage post-send.
  const freshId = React.useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("fresh");
  }, [location.search]);

  // Row-level visual highlight for the fresh send (Zuri spec §3.7 hook).
  const [freshHighlightId, setFreshHighlightId] = React.useState(freshId);

  React.useEffect(() => {
    if (!freshId) return undefined;
    setFreshHighlightId(freshId);
    const t = window.setTimeout(() => {
      setFreshHighlightId(null);
    }, FRESH_HIGHLIGHT_MS);
    return () => window.clearTimeout(t);
  }, [freshId]);

  // List state.
  const [broadcasts, setBroadcasts] = React.useState([]);
  const [nextCursor, setNextCursor] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Drawer state.
  const [openDetailId, setOpenDetailId] = React.useState(null);

  // Fetcher — shared by initial load, refresh, and load-more.
  const fetchPage = React.useCallback(
    async ({ cursor = null, mode = "initial" } = {}) => {
      if (mode === "initial") setLoading(true);
      if (mode === "refresh") setRefreshing(true);
      if (mode === "load-more") setLoadingMore(true);
      setError(null);
      try {
        const filters = { limit: PAGE_LIMIT };
        if (cursor) filters.cursor = cursor;
        const res = await broadcastAPI.list(filters);
        const newRows = res?.data?.broadcasts || [];
        const newCursor = res?.data?.nextCursor || null;
        if (mode === "load-more") {
          setBroadcasts((prev) => [...prev, ...newRows]);
        } else {
          setBroadcasts(newRows);
        }
        setNextCursor(newCursor);
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.userMessage ||
          "Couldn't load broadcast history. Try again in a moment.";
        setError(message);
        if (mode !== "initial") {
          toast.error(message, { autoClose: 4000 });
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Initial load.
  React.useEffect(() => {
    fetchPage({ mode: "initial" });
  }, [fetchPage]);

  const handleRefresh = () => fetchPage({ mode: "refresh" });
  const handleLoadMore = () =>
    fetchPage({ mode: "load-more", cursor: nextCursor });
  const handleRetry = () => fetchPage({ mode: "initial" });

  return (
    <div className="broadcast-history-page">
      {isAdmin && (
        <div
          className="broadcast-history-admin-mode-bar"
          role="status"
          aria-label="Admin mode"
        />
      )}

      <header className="broadcast-history-page-header">
        <h1>Broadcast History</h1>
        <p className="broadcast-history-page-subtitle">
          Past communications you've sent ({STUDIO_TZ_LABEL})
          {!isAdmin && " — your sends only"}
        </p>
        <div className="broadcast-history-page-actions">
          <button
            type="button"
            className="broadcast-history-btn-outline"
            onClick={() => navigate("/staff/communications/broadcast")}
            aria-label="Compose a new broadcast"
          >
            <FaPaperPlane aria-hidden="true" /> Compose
          </button>
          <button
            type="button"
            className="broadcast-history-btn-outline"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            aria-label="Refresh broadcast history"
          >
            <FaSync
              aria-hidden="true"
              className={refreshing ? "broadcast-history-spin" : ""}
            />{" "}
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      <main className="broadcast-history-page-body">
        {loading && (
          <div
            className="broadcast-history-skeleton"
            role="status"
            aria-live="polite"
            aria-label="Loading broadcast history"
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="broadcast-history-skeleton-row"
                aria-hidden="true"
              />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="broadcast-history-error" role="alert">
            <FaExclamationTriangle
              className="broadcast-history-error-icon"
              aria-hidden="true"
            />
            <div className="broadcast-history-error-title">
              Couldn't load broadcast history
            </div>
            <div className="broadcast-history-error-body">{error}</div>
            <button
              type="button"
              className="broadcast-history-btn-primary"
              onClick={handleRetry}
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && broadcasts.length === 0 && (
          <div className="broadcast-history-empty" role="status">
            <FaPaperPlane
              className="broadcast-history-empty-icon"
              aria-hidden="true"
            />
            <div className="broadcast-history-empty-title">
              No broadcasts yet
            </div>
            <div className="broadcast-history-empty-body">
              Send your first broadcast from the Send Communication page.
            </div>
            <button
              type="button"
              className="broadcast-history-btn-primary"
              onClick={() => navigate("/staff/communications/broadcast")}
            >
              <FaPaperPlane aria-hidden="true" /> Send Communication
            </button>
          </div>
        )}

        {!loading && !error && broadcasts.length > 0 && (
          <>
            <div className="broadcast-history-table-wrap">
              <table
                className="broadcast-history-table"
                aria-label="Broadcast history"
              >
                <thead>
                  <tr>
                    <th scope="col">Sent</th>
                    <th scope="col">Sender</th>
                    <th scope="col">Subject</th>
                    <th scope="col">Template / Freeform</th>
                    <th scope="col" className="broadcast-history-th-num">
                      Recipients
                    </th>
                    <th scope="col" className="broadcast-history-th-num">
                      Open rate
                    </th>
                    <th scope="col" className="broadcast-history-th-num">
                      Click rate
                    </th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {broadcasts.map((b) => {
                    const pill = statusPillFor(b);
                    const sub = statsSubline(b);
                    const sender = formatSenderLabel(b, viewerUserId);
                    const stamp = splitPhoenixDateTime(b.createdAt);
                    const isFresh =
                      freshHighlightId &&
                      String(freshHighlightId) === String(b.broadcastLogId);
                    return (
                      <tr
                        key={b.broadcastLogId}
                        className={`broadcast-history-row${
                          isFresh ? " broadcast-history-row--fresh" : ""
                        }`}
                        onClick={() => setOpenDetailId(b.broadcastLogId)}
                        tabIndex={0}
                        role="button"
                        aria-label={`Open detail for broadcast sent ${stamp.date} ${stamp.time}: ${b.subject || "no subject"}`}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setOpenDetailId(b.broadcastLogId);
                          }
                        }}
                      >
                        <td>
                          <div className="broadcast-history-cell-date">
                            {stamp.date || "—"}
                          </div>
                          <div className="broadcast-history-cell-time">
                            {stamp.time}
                          </div>
                        </td>
                        <td>
                          <span data-testid="row-sender">{sender}</span>
                        </td>
                        <td title={b.subject || ""}>
                          {truncateSubject(b.subject)}
                        </td>
                        <td>
                          {b.composeMode === "template"
                            ? "Template"
                            : b.composeMode === "freeform"
                            ? "Freeform"
                            : "—"}
                        </td>
                        <td className="broadcast-history-cell-num">
                          {b.recipientCount ?? 0}
                        </td>
                        <td
                          className="broadcast-history-cell-num"
                          data-testid="row-open-rate"
                        >
                          —
                        </td>
                        <td
                          className="broadcast-history-cell-num"
                          data-testid="row-click-rate"
                        >
                          —
                        </td>
                        <td>
                          <span
                            className={`broadcast-history-pill broadcast-history-pill--${pill.tone}`}
                            aria-label={`Status: ${pill.label}`}
                          >
                            {pill.tone === "delivered" ? (
                              <FaCheckCircle aria-hidden="true" />
                            ) : pill.tone === "failed" ? (
                              <FaExclamationTriangle aria-hidden="true" />
                            ) : null}{" "}
                            {pill.label}
                          </span>
                          {sub && (
                            <div className="broadcast-history-cell-substats">
                              {sub}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div
              className="broadcast-history-footer-summary"
              aria-live="polite"
            >
              Showing {broadcasts.length}
              {nextCursor ? " — more available" : ""}
            </div>

            {nextCursor && (
              <div className="broadcast-history-load-more">
                <button
                  type="button"
                  className="broadcast-history-btn-outline"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  aria-label="Load more broadcasts"
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {openDetailId && (
        <BroadcastDetailDrawer
          broadcastLogId={openDetailId}
          onClose={() => setOpenDetailId(null)}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};

export default BroadcastHistoryPage;
